/** Helpers for Frappe /api/method responses + standalone client RPC. */

import { apiUrl, methodUrl as configMethodUrl, resolveSiteName } from './config'

export { apiUrl }
export function methodUrl(method) {
  return configMethodUrl(method)
}

export function unwrap(data) {
  if (data == null) return data
  if (typeof data !== 'object') return data

  // Normalized by frappeFetchPatch: { data, message }
  if (data.data != null && data.message != null) {
    if (data.data?.boot) return data.data
    return data.data
  }
  if (data.data != null && (data.success === true || data.message != null)) {
    return data.data
  }
  if (data.message != null && typeof data.message === 'object' && !Array.isArray(data.message)) {
    if (
      'authenticated' in data.message ||
      'plan_code' in data.message ||
      data.message.docs ||
      data.message.boot
    ) {
      return data.message
    }
  }
  if (data.boot && typeof data.boot === 'object') {
    return data
  }
  return data
}

export function siteName(bootSite) {
  return resolveSiteName(bootSite)
}

/**
 * Direct RPC that always returns Frappe `message`.
 */
function csrfHeaders(extra = {}) {
  const headers = {
    Accept: 'application/json',
    'X-Frappe-Site-Name': siteName(),
    ...extra,
  }
  if (window.csrf_token && window.csrf_token !== '{{ csrf_token }}') {
    headers['X-Frappe-CSRF-Token'] = window.csrf_token
  }
  return headers
}

/**
 * GET RPC — CSRF is not checked on GET. Use for boot so a leftover Desk
 * `sid` cookie (same host, different port) does not block the first call.
 */
export async function rpcGet(method, args = {}) {
  const url = new URL(methodUrl(method), window.location.origin)
  for (const [k, v] of Object.entries(args || {})) {
    if (v === undefined || v === null || v === '') continue
    url.searchParams.set(k, String(v))
  }
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: csrfHeaders(),
    credentials: 'include',
  })
  return parseRpcResponse(res)
}

export async function rpc(method, args = {}) {
  const res = await fetch(methodUrl(method), {
    method: 'POST',
    headers: csrfHeaders({ 'Content-Type': 'application/json; charset=utf-8' }),
    credentials: 'include',
    body: JSON.stringify(args || {}),
  })
  return parseRpcResponse(res)
}

async function parseRpcResponse(res) {

  let body = null
  try {
    body = await res.json()
  } catch {
    body = null
  }

  if (!res.ok) {
    // Glamora-style envelope: { message, code?, details? }. Older Frappe errors may
    // still carry _server_messages / exc_type; never surface tracebacks.
    const csrfFail = body?.exc_type === 'CSRFTokenError' || /csrf/i.test(String(body?.exc_type || ''))
    let messages = []
    if (body?._server_messages) {
      try {
        messages = JSON.parse(body._server_messages).map((m) => {
          try {
            return JSON.parse(m).message
          } catch {
            return m
          }
        })
      } catch {
        /* ignore */
      }
    }
    if (typeof body?.message === 'string') messages.push(body.message)
    else if (Array.isArray(body?.message)) messages.push(...body.message.map(String))
    messages = [...new Set(messages.map((m) => String(m || '').trim()).filter(Boolean))]
    const err = new Error(
      csrfFail
        ? 'Session expired. Refresh the page and sign in again.'
        : messages.join(' ') || body?._error_message || `Request failed (${res.status})`,
    )
    err.status = res.status
    err.code = body?.code || null
    err.details = body?.details || null
    err.messages = csrfFail ? [err.message] : messages
    err.data = body
    throw err
  }

  if (body && typeof body === 'object') {
    // Catolica success_response puts payload in `data` and a status string in `message`.
    // Classic Frappe methods put the real return value in `message` only.
    if (body.data !== undefined && body.data !== null) {
      if (body.message === undefined || typeof body.message === 'string') {
        return body.data
      }
    }
    if (body.message !== undefined) return body.message
    if (body.data !== undefined) return body.data
  }
  return body
}

export function displayName(row) {
  if (!row) return ''
  if (row.member_name) return row.member_name
  const parts = [row.first_name, row.middle_name, row.last_name].filter(Boolean)
  return parts.join(' ') || row.name || ''
}

/**
 * Attach Member.photo (and User.user_image) onto list rows for avatar columns.
 * Mutates and returns the rows array.
 */
export async function enrichMemberPhotos(rows, { idField = 'member' } = {}) {
  if (!Array.isArray(rows) || !rows.length) return rows
  const ids = [...new Set(rows.map((r) => r[idField]).filter(Boolean))]
  if (!ids.length) return rows
  try {
    const list = await rpc('frappe.client.get_list', {
      doctype: 'Member',
      fields: ['name', 'photo'],
      filters: [['name', 'in', ids]],
      limit_page_length: ids.length,
    })
    const map = {}
    for (const m of Array.isArray(list) ? list : []) {
      map[m.name] = m.photo || null
    }
    for (const r of rows) {
      const id = r[idField]
      if (id && map[id] != null) {
        r.photo = map[id]
        r.image = map[id]
      }
    }
  } catch {
    /* non-fatal — tables still render without photos */
  }
  return rows
}

export function formatMoney(n, currency = 'KES') {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number(n || 0))
  } catch {
    return String(n ?? 0)
  }
}

export function formatDate(d) {
  if (!d) return '—'
  return String(d).slice(0, 10)
}

export function formatTime(t) {
  if (!t) return '—'
  const value = String(t)
  const match = value.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return value
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

export function companyFilter(session, extra = {}) {
  const filters = { ...extra }
  if (session.boot?.company) filters.company = session.boot.company
  return filters
}

export function errorMessage(e) {
  if (!e) return 'Something went wrong'
  if (typeof e === 'string') return e
  if (e.messages?.length) return e.messages.join(' ')
  if (e.message) return e.message
  if (e.exc) return String(e.exc).slice(0, 200)
  return 'Something went wrong'
}
